'use client';

import { useId, useMemo } from 'react';
import { motion } from 'framer-motion';
import { entrance, type FlowLink, type FlowNode } from '@/src/shared/lib/chart';

export const FLOW_RIBBON_OPACITY = 0.34;

interface FlowRibbonsProps {
  nodes: FlowNode[];
  links: FlowLink[];
  nodeWidth: number;
  /** Vertical extent the curtains cover, in the ribbons' own coordinates. */
  top: number;
  height: number;
  reduced: boolean;
  /** Seconds before a ribbon starts to uncover. */
  delayOf: (link: FlowLink, index: number) => number;
  opacityOf?: (link: FlowLink) => number;
}

/**
 * The ribbons of a flow chart, filled from the source's colour to the target's.
 * Each has its own curtain, so they uncover left to right one after another;
 * under reduced motion they render uncovered.
 */
export function FlowRibbons({
  nodes,
  links,
  nodeWidth,
  top,
  height,
  reduced,
  delayOf,
  opacityOf = () => FLOW_RIBBON_OPACITY,
}: FlowRibbonsProps) {
  const uid = useId();
  const byId = useMemo(() => new Map(nodes.map((n) => [n.id, n])), [nodes]);
  const key = (l: FlowLink) => `${l.source}>${l.target}`;

  return (
    <>
      <defs>
        {links.map((l, i) => {
          const source = byId.get(l.source)!;
          const target = byId.get(l.target)!;
          const x0 = source.x + nodeWidth;
          return (
            <g key={key(l)}>
              <linearGradient
                id={`${uid}-g${i}`}
                gradientUnits="userSpaceOnUse"
                x1={x0}
                x2={target.x}
              >
                <stop offset="0%" stopColor={source.color} />
                <stop offset="100%" stopColor={target.color} />
              </linearGradient>
              <clipPath id={`${uid}-c${i}`}>
                <motion.rect
                  x={x0}
                  y={top}
                  height={height}
                  {...entrance('sweep', { reduced, delay: delayOf(l, i), width: target.x - x0 })}
                />
              </clipPath>
            </g>
          );
        })}
      </defs>
      {links.map((l, i) => (
        <path
          key={key(l)}
          d={l.path}
          fill={`url(#${uid}-g${i})`}
          clipPath={`url(#${uid}-c${i})`}
          opacity={opacityOf(l)}
          className="transition-opacity duration-200"
          data-flow-ribbon={key(l)}
        />
      ))}
    </>
  );
}
